import { useState } from 'react';
import { Star } from 'lucide-react';
import type { MetricCatalogEntry } from '@monitoring-panel/types';
import { cn } from '@/lib/utils';
import { TableFrame, Th, Td, NumTd } from './TableFrame';
import { dayMonth, eur, formatMetric, fullDate, num, pct } from './format';
import type { DeliveryReport, MetricTotals, MetricValues, ReportLine, ReportPlan } from './types';

interface CardLineMetricsProps {
  report: DeliveryReport;
  reportDate: string;
}

/** Metric columns that fit next to the line name in the card's side panel without scrolling sideways. */
const MAX_METRICS = 3;

/** Línea (the rest) · Tope · % gasto · one column per chosen metric. */
const FIXED_COLUMN_WIDTHS = [undefined, '80px', '64px'];
const METRIC_COLUMN_WIDTH = '80px';

/** Pseudo-metric shown instead of the plan's Total Media Cost: what the line leaves after its cost. */
const MARGIN_ID = 'margin';
const MEDIA_COST_ID = 'cost';

interface Column {
  id: string;
  label: string;
}

type PeriodId = 'yesterday' | 'last7' | 'campaign';

interface Period {
  id: PeriodId;
  label: string;
  /** How many of the report's last days the period sums; null is the whole campaign. */
  days: number | null;
}

const PERIODS = [
  { id: 'yesterday', label: 'Ayer', days: 1 },
  { id: 'last7', label: 'Últimos 7 días', days: 7 },
  { id: 'campaign', label: 'Total campaña', days: null },
] as const satisfies readonly Period[];

const DEFAULT_PERIOD: PeriodId = 'yesterday';

const TOTAL_KEYS = ['imp', 'clk', 'vw', 'meas', 'conv', 'vs', 'q1', 'q2', 'q3', 'cv', 'cost', 'vimp'] as const satisfies readonly (keyof MetricTotals)[];

/** How the skill computes a metric from the raw sums: num / den × scale, or num alone when den is null. */
interface MetricFormula {
  num: keyof MetricTotals;
  den: keyof MetricTotals | null;
  scale: number;
}

interface LineFigures {
  m: MetricValues;
  /** Share of the period's spend, %. */
  share: number | null;
  units: number;
  cost: number | null;
}

/** What the table shows for one period: each line's metrics plus what the margins are worked out from. */
interface PeriodFigures {
  lines: Map<string, LineFigures>;
  total: MetricValues;
  revenue: number | null;
  cost: number | null;
}

/** The plan's metrics with the margin in place of Total Media Cost. */
function selectableColumns(plan: ReportPlan): Column[] {
  const metrics = plan.metrics.map((metric) => ({ id: metric.id, label: metric.label }));
  const costIndex = metrics.findIndex((metric) => metric.id === MEDIA_COST_ID);
  const margin = { id: MARGIN_ID, label: 'Margen' };
  if (costIndex === -1) return [...metrics, margin];
  return [...metrics.slice(0, costIndex), margin, ...metrics.slice(costIndex + 1)];
}

/** The primary metric and the margin first, then the plan's order, up to MAX_METRICS. */
function defaultColumns(plan: ReportPlan, columns: Column[]): string[] {
  const ids = columns.map((column) => column.id);
  const first = [plan.primary, MARGIN_ID].filter((id): id is string => !!id && ids.includes(id));
  return [...first, ...ids.filter((id) => !first.includes(id))].slice(0, MAX_METRICS);
}

/** Daily cap without the "/día" the column header already says. */
function capText(line: ReportLine): string {
  if (line.cap_now !== null) return eur(line.cap_now, 0);
  if (line.cap_imp !== null) return `${num(line.cap_imp)} impr.`;
  return 'sin tope';
}

/** The report's accumulated figures, as the skill computed them. */
function campaignFigures(report: DeliveryReport): PeriodFigures {
  const lines = new Map(
    report.lines.map((line) => [line.id, { m: line.m, share: line.share, units: line.units ?? 0, cost: line.cost }])
  );
  return { lines, total: report.camp.m, revenue: report.delivery.revenue, cost: report.delivery.cost };
}

function sumTotals(totals: MetricTotals[]): MetricTotals {
  const sum = Object.fromEntries(TOTAL_KEYS.map((key) => [key, 0])) as unknown as MetricTotals;
  for (const day of totals) {
    for (const key of TOTAL_KEYS) sum[key] += day[key] ?? 0;
  }
  return sum;
}

/** Every catalog metric from raw sums, with the skill's formula (`metric_value` in delivery_report.py). */
function metricValues(catalog: Record<string, MetricCatalogEntry>, totals: MetricTotals): MetricValues {
  return Object.fromEntries(
    Object.entries(catalog).map(([metricId, entry]) => {
      const { num: numerator, den: denominator, scale } = entry as MetricCatalogEntry & MetricFormula;
      if (denominator === null) return [metricId, totals[numerator]];
      return [metricId, totals[denominator] ? (totals[numerator] / totals[denominator]) * scale : null];
    })
  );
}

/** Each line's figures summed over the given days, from the raw sums the report keeps per line and day. */
function windowFigures(report: DeliveryReport, dates: string[]): PeriodFigures {
  const inWindow = new Set(dates);
  const perLine = report.lines.map((line) => {
    const days = line.daily.filter((day) => inWindow.has(day.date));
    return {
      id: line.id,
      totals: sumTotals(days.flatMap((day) => (day.t ? [day.t] : []))),
      units: days.reduce((sum, day) => sum + (day.units ?? 0), 0),
    };
  });
  const all = sumTotals(perLine.map((line) => line.totals));
  const executionDays = (report.delivery.daily ?? []).filter((day) => inWindow.has(day.date));
  const lines = new Map(
    perLine.map(({ id, totals, units }) => [
      id,
      {
        m: metricValues(report.metrics_catalog, totals),
        share: all.cost ? (totals.cost / all.cost) * 100 : null,
        units,
        cost: totals.cost,
      },
    ])
  );
  const revenue = executionDays.length ? executionDays.reduce((sum, day) => sum + (day.revenue ?? 0), 0) : null;
  return { lines, total: metricValues(report.metrics_catalog, all), revenue, cost: all.cost };
}

/** The report's last `days` days; the last one is the day before the report. */
function periodDates(report: DeliveryReport, period: Period): string[] {
  return period.days === null ? [] : report.days.slice(-period.days);
}

function periodFigures(report: DeliveryReport, period: Period): PeriodFigures {
  return period.days === null ? campaignFigures(report) : windowFigures(report, periodDates(report, period));
}

/** "Ayer (27/10)", "Últimos 7 días (21/10–27/10)" or "Acumulado de la campaña". */
function periodText(period: Period, dates: string[]): string {
  if (period.days === null) return 'Acumulado de la campaña';
  if (dates.length === 0) return period.label;
  const first = dayMonth(dates[0]);
  const last = dayMonth(dates[dates.length - 1]);
  return `${period.label} (${first === last ? last : `${first}–${last}`})`;
}

/**
 * Margin of each line as a % of its revenue. Revenue is only known for the
 * whole execution, so each line gets the share of it its served units earn;
 * the lines then add up to the execution's margin.
 */
function lineMargins(figures: PeriodFigures): { byLine: Map<string, number | null>; total: number | null } {
  const { revenue, cost } = figures;
  const totalUnits = [...figures.lines.values()].reduce((sum, line) => sum + line.units, 0);
  const byLine = new Map<string, number | null>();
  for (const [lineId, line] of figures.lines) {
    const lineRevenue = revenue && totalUnits ? (revenue * line.units) / totalUnits : 0;
    byLine.set(lineId, lineRevenue > 0 && line.cost !== null ? (1 - line.cost / lineRevenue) * 100 : null);
  }
  const total = revenue && cost !== null ? (1 - cost / revenue) * 100 : null;
  return { byLine, total };
}

/**
 * KPIs per line of the latest report, for the side panel of an execution
 * card: yesterday, the last 7 days or the whole campaign, with the plan's
 * metrics picked with chips, a few at a time, so the table always fits the
 * panel's width.
 */
export function CardLineMetrics({ report, reportDate }: CardLineMetricsProps) {
  const { lines, plan, metrics_catalog: catalog } = report;
  const available = selectableColumns(plan);
  const [chosen, setChosen] = useState<string[]>(() => defaultColumns(plan, available));
  const [periodId, setPeriodId] = useState<PeriodId>(DEFAULT_PERIOD);
  const period = PERIODS.find((candidate) => candidate.id === periodId) ?? PERIODS[PERIODS.length - 1];
  const figures = periodFigures(report, period);
  const navIds = new Set(plan.nav.line_ids);
  const columns = available.filter((column) => chosen.includes(column.id));
  const full = chosen.length >= MAX_METRICS;
  const margins = lineMargins(figures);

  const toggle = (columnId: string) => {
    setChosen((current) =>
      current.includes(columnId) ? current.filter((id) => id !== columnId) : [...current, columnId]
    );
  };

  const lineValue = (line: ReportLine, columnId: string): string =>
    columnId === MARGIN_ID
      ? pct(margins.byLine.get(line.id), 0)
      : formatMetric(catalog, columnId, figures.lines.get(line.id)?.m[columnId]);
  const totalValue = (columnId: string): string =>
    columnId === MARGIN_ID ? pct(margins.total, 0) : formatMetric(catalog, columnId, figures.total[columnId]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Periodo">
        {PERIODS.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            onClick={() => setPeriodId(candidate.id)}
            aria-pressed={candidate.id === periodId}
            className={cn('adv-chip', candidate.id === periodId && 'adv-chip--on')}
          >
            {candidate.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Métricas">
        {available.map((column) => {
          const on = chosen.includes(column.id);
          return (
            <button
              key={column.id}
              type="button"
              onClick={() => toggle(column.id)}
              disabled={!on && full}
              aria-pressed={on}
              title={!on && full ? `Máximo ${MAX_METRICS} métricas: quita una para añadir esta` : undefined}
              className={cn('adv-chip gap-1 disabled:opacity-50 disabled:cursor-not-allowed', on && 'adv-chip--on')}
            >
              {plan.primary === column.id && <Star size={12} aria-label="primaria" />}
              {column.label}
            </button>
          );
        })}
      </div>

      <TableFrame
        columnWidths={[...FIXED_COLUMN_WIDTHS, ...columns.map(() => METRIC_COLUMN_WIDTH)]}
        head={
          <>
            <Th>Línea</Th>
            <Th numeric>Tope</Th>
            <Th numeric>% gasto</Th>
            {columns.map((column) => (
              <Th key={column.id} numeric>
                <span className="block truncate" title={column.label}>
                  {column.label}
                </span>
              </Th>
            ))}
          </>
        }
      >
        {lines.map((line) => (
          <tr key={line.id} className="hover:bg-adv-sunken">
            <Td>
              <span className="font-bold break-words">{line.name}</span>
              {navIds.has(line.id) && <span className="adv-tag adv-tag--gold ml-2">Nav. Activa</span>}
              {line.status !== 'Activa' && (
                <p className="text-adv-body-s text-adv-tertiary">{line.status.toLowerCase()}</p>
              )}
            </Td>
            <NumTd>{capText(line)}</NumTd>
            <NumTd>{pct(figures.lines.get(line.id)?.share, 0)}</NumTd>
            {columns.map((column) => (
              <NumTd key={column.id}>{lineValue(line, column.id)}</NumTd>
            ))}
          </tr>
        ))}
        <tr className="bg-adv-sunken font-bold">
          <Td>Total</Td>
          <Td />
          <NumTd>100 %</NumTd>
          {columns.map((column) => (
            <NumTd key={column.id}>{totalValue(column.id)}</NumTd>
          ))}
        </tr>
      </TableFrame>

      <p className="text-adv-body-s text-adv-tertiary">
        {periodText(period, periodDates(report, period))} · informe del {fullDate(reportDate)} · margen de cada línea
        con los ingresos repartidos por sus unidades servidas
      </p>
    </div>
  );
}
